import React, { ReactElement, useEffect, useState } from 'react';
import { HeadTag } from 'shared/components';
import {
  Box,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Formik, FormikHelpers } from 'formik';
import {
  AppFormikSelect,
  AppFormikSelectWithKeys,
  AppPrimaryButton,
} from 'shared/components/AppComponents';
import EmptyPlaceholder from 'shared/components/EmptyPlaceholder';
import PageHeader from 'shared/components/PageHeader';
import { TierApproverService } from 'services/TierApprovers';
import { Toast, Utils } from 'shared/helpers';
import DeleteIcon from '@mui/icons-material/Delete';
import { ManageTeamService } from 'services/ManageTeamService';
import { IInvitation } from 'modules/settings/management/manage-team/components/types';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { useNavigate } from 'react-router-dom';
import { BaseApplicationState } from 'state/baseapplication/Reducer';
import { useSelector } from 'react-redux';

export interface IUser {
  _id: string;
  email: string;
  name: string;
}

export interface ITierApprover {
  _id: string;
  approverId: IUser;
  riskTier: string;
  tenantId: string;
  createdBy: any;
  updatedBy: any;
  updatedDate: any;
  isActive: boolean;
  deletedAt: any;
  createdDate: string;
  __v: number;
}

const TierApprovers = (): ReactElement => {
  const navigate = useNavigate();
  const { orgACLConfig }: BaseApplicationState = useSelector(
    (state: any) => state.baseAppState
  );

  const [isLoading, setIsLoading] = useState(false);
  const [tierApprovers, setTierApprovers] = useState<ITierApprover[]>([]);
  const [teamMembers, setTeamMembers] = useState<IInvitation[]>([]);
  // API Calls
  const loadTeamMembers = (): void => {
    const queryString = new URLSearchParams({
      offset: '0',
      limit: '1000',
      search: '',
    }).toString();
    ManageTeamService.getAuditorGovernanceReviewers(queryString).then(
      (response) => {
        const { user_list } = response.data.data;
        if (user_list.length > 0) {
          setTeamMembers(user_list);
        }
      }
    );
  };
  const createTierApprover = (payload: any, callback: any): void => {
    TierApproverService.create(payload)
      .then((response) => {
        Toast.showSuccessMessage(
          `User successfully added as ${payload.riskTier} risk tier approver`
        );
        callback(response);
      })
      .catch((error) => {
        callback(false);
        Utils.handleErrorResponse(error);
      });
  };
  const loadTierApprovers = (): void => {
    setIsLoading(true);
    TierApproverService.getAll()
      .then((response) => {
        const { data } = response.data;
        setTierApprovers(data);
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };
  const deleteTierApprover = (id: string): void => {
    TierApproverService.deleteRecord(id)
      .then(() => {
        Toast.showSuccessMessage('Approver successfully removed');
        loadTierApprovers();
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
      });
  };

  // UI Actions
  const actionOnSubmit = (
    formValues: any,
    formik: FormikHelpers<any>
  ): void => {
    createTierApprover({ ...formValues }, (response: any) => {
      formik.setSubmitting(false);
      if (typeof response === 'object') {
        formik.resetForm();
        loadTierApprovers();
      }
    });
  };
  const actionOnDelete = (approver: ITierApprover): void => {
    deleteTierApprover(approver._id);
  };

  // Hooks
  useEffect(() => {
    loadTierApprovers();
    loadTeamMembers();
  }, []);

  // UI Elements
  return (
    <>
      <PageHeader
        title="Risk Tier Approvers"
        leftBackButton={
          <IconButton
            onClick={() => {
              navigate('/settings?tabIndex=2');
            }}
          >
            <ArrowBackIosIcon />
          </IconButton>
        }
      />
      <Box className="risk-management tier-approvers">
        <HeadTag title="Tier Approvers" />

        <Box className="tier-approvers-wrapper">
          <Grid container className="tier-approvers-container">
            <Grid item md={4} xs={12}>
              <Paper variant="outlined">
                <Typography variant="h4">Add new approver</Typography>
                <Typography component="span">
                  Assign users who can approve AI inventory items based on risk
                  tier
                </Typography>
                <Formik
                  initialValues={{
                    approverId: '',
                    riskTier: 'Low',
                  }}
                  onSubmit={(formValues, formik) => {
                    actionOnSubmit(formValues, formik);
                  }}
                >
                  {({ isSubmitting, handleSubmit }) => (
                    <>
                      <Stack direction="column" spacing={3}>
                        <AppFormikSelectWithKeys
                          name="approverId"
                          className="rework-selection"
                          notched={false}
                          labelId="fix-selection-label"
                          testid="solicitor-dropdown"
                          MenuProps={{
                            className: 'menu-fix-selection single-fix-rework',
                          }}
                          outerLabel
                          placeholder="Select User"
                          notEmphasized
                          notSelectablePlaceholder
                          label="User"
                          titleKey="name"
                          valueKey="_id"
                          items={teamMembers}
                        />
                        <AppFormikSelect
                          name="riskTier"
                          className="rework-selection"
                          notched={false}
                          labelId="fix-selection-label"
                          testid="solicitor-dropdown"
                          MenuProps={{
                            className: 'menu-fix-selection single-fix-rework',
                          }}
                          outerLabel
                          placeholder="Select Risk"
                          notEmphasized
                          notSelectablePlaceholder
                          label="Risk Tier"
                          items={['Low', 'Moderate', 'High', 'Critical']}
                        />
                      </Stack>
                      {orgACLConfig.riskManagement?.includes(
                        'riskManagement.tierApprovers.addApprover'
                      ) && (
                        <AppPrimaryButton
                          isLoading={isSubmitting}
                          onClick={handleSubmit}
                          title="Add approver"
                        />
                      )}
                    </>
                  )}
                </Formik>
              </Paper>
            </Grid>
            <Grid item md={8} xs={12}>
              {isLoading && (
                <Box className="empty-content">
                  <CircularProgress />
                </Box>
              )}
              {!isLoading && (
                <>
                  <Typography variant="h4">Current approvers</Typography>
                  <Typography component="span">
                    Users who can approve AI inventory items by risk tier
                  </Typography>

                  <TableContainer>
                    <Table className="app-table">
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Email</TableCell>
                          <TableCell>Risk tier</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {tierApprovers.map((row: any) => (
                          <TableRow
                            key={row._id}
                            sx={{
                              '&:last-child td, &:last-child th': {
                                border: 0,
                              },
                            }}
                          >
                            <TableCell component="th" scope="row">
                              {row.approverId.name}
                            </TableCell>
                            <TableCell component="th" scope="row">
                              {row.approverId.email}
                            </TableCell>
                            <TableCell>{row.riskTier}</TableCell>
                            <TableCell>
                              {orgACLConfig.riskManagement?.includes(
                                'riskManagement.tierApprovers.deleteApprover'
                              ) && (
                                <IconButton onClick={() => actionOnDelete(row)}>
                                  <DeleteIcon />
                                </IconButton>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                        {tierApprovers.length < 1 && (
                          <TableRow>
                            <TableCell colSpan={3}>
                              <EmptyPlaceholder title="No approvers configured" />
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </Grid>
          </Grid>
        </Box>
      </Box>
    </>
  );
};

export default TierApprovers;
